import React from 'react';
import { Ban, Eye, EyeOff, Flag, Pencil, Pill } from 'lucide-react';
import Sheet from '../../../common/Sheet';

export interface EntryAction {
  key: 'edit' | 'effect' | 'visibility' | 'flag' | 'invalidate';
  label: string;
  hint?: string;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

const ICONS: Record<EntryAction['key'], React.ReactNode> = {
  edit: <Pencil size={18} aria-hidden="true" />,
  effect: <Pill size={18} aria-hidden="true" />,
  visibility: <Eye size={18} aria-hidden="true" />,
  flag: <Flag size={18} aria-hidden="true" />,
  invalidate: <Ban size={18} aria-hidden="true" />,
};

/**
 * Phase 18U U10: the actions of one chart entry as 48 px labelled rows in a bottom sheet
 * (replaces the 14 px "⋮" menu that had no Escape or outside-click close). Escape, the backdrop
 * and Close dismiss it; focus returns to the Actions button.
 */
const EntryActionsSheet: React.FC<{ open: boolean; onClose: () => void; title: string; subtitle?: string; actions: EntryAction[] }> = ({
  open, onClose, title, subtitle, actions,
}) => (
  <Sheet open={open} onClose={onClose} title={title} subtitle={subtitle} variant="bottom" testId="entry-actions-sheet">
    <ul className="divide-y divide-gray-100" role="menu" aria-label={`${title} actions`}>
      {actions.map((a) => (
        <li key={a.key} role="none">
          <button
            type="button"
            role="menuitem"
            disabled={a.disabled}
            onClick={() => { onClose(); a.onSelect(); }}
            className={`flex min-h-[48px] w-full items-center gap-3 px-1 text-left text-base disabled:cursor-not-allowed disabled:opacity-50 ${a.destructive ? 'text-red-700' : 'text-gray-900'}`}
          >
            {a.key === 'visibility' && a.label.startsWith('Make internal') ? <EyeOff size={18} aria-hidden="true" /> : ICONS[a.key]}
            <span className="min-w-0">
              <span className="block font-medium">{a.label}</span>
              {a.hint && <span className="block text-xs text-gray-600">{a.hint}</span>}
            </span>
          </button>
        </li>
      ))}
    </ul>
  </Sheet>
);

export default EntryActionsSheet;
